import { useCallback } from 'react';

import { NotificationViewModel } from '@/@types/client-api';
import { IContentExtended } from '@/lib/helpers/expandChildren';
import { useLazyGetContentByFriendlyUrlQuery } from '@/redux/api/contentDeliveryAPI';
import { NotifyPanelItems } from '@/redux/slices/pdpSlice';
import { simpleHash } from '@/utils/helpers';

type NotificationContent = IContentExtended & {
  notificationViewModel?: NotificationViewModel;
};

const getFriendlyUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return url;
  }
};

export const mergeNotifyMeItems = (
  notifyItems: NotifyPanelItems | undefined,
  notificationDetails: NotificationViewModel | undefined
): NotifyPanelItems | undefined => {
  if (!notificationDetails) return notifyItems;

  return {
    ...notifyItems,
    url: notificationDetails.fullUrl ?? notifyItems?.url,
    buttonLabel: notificationDetails.buttonLabel ?? notifyItems?.buttonLabel,
    messageLabel: notificationDetails.bodyText ?? notifyItems?.messageLabel,
    headerLabel: notificationDetails.headerText ?? notifyItems?.headerLabel,
    successHeaderLabel: notificationDetails.successHeaderText ?? notifyItems?.successHeaderLabel,
    successMessageLabel: notificationDetails.successBodyText ?? notifyItems?.successMessageLabel,
    source: notificationDetails.source ?? notifyItems?.source
  };
};

export const useResolveNotifyMeItems = () => {
  const [getContentByFriendlyUrl] = useLazyGetContentByFriendlyUrlQuery();

  return useCallback(
    async (
      url: string | undefined,
      notifyItems?: NotifyPanelItems
    ): Promise<NotifyPanelItems | undefined> => {
      if (notifyItems?.source || !url) return notifyItems;

      try {
        const content = (await getContentByFriendlyUrl(
          {
            friendlyUrl: getFriendlyUrl(url),
            userId: simpleHash('ANON')
          },
          true
        ).unwrap()) as NotificationContent;

        return mergeNotifyMeItems(notifyItems, content.notificationViewModel);
      } catch {
        return notifyItems;
      }
    },
    [getContentByFriendlyUrl]
  );
};

export default useResolveNotifyMeItems;
